import { useState, useEffect, useRef } from 'react';

/**
 * Hook for managing incubation timer.
 * Returns current progress (0-100).
 *
 * @param onComplete   Called once when incubation completes.
 * @param durationMs   Total incubation duration (default 120 000 ms = 2 min).
 * @param anchorEpoch  Epoch ms (Date.getTime()) of the moment incubation started.
 *                     null means the timer is inactive (progress stays at 0).
 *                     The effect re-runs whenever this value changes, ensuring the
 *                     timer always resets to the correct origin even when the
 *                     computed elapsed happens to be 0 ms both before and after
 *                     the anchor switches (e.g. specimenStartedAt replacing
 *                     baselineCompletedAt immediately after baseline completes).
 */
export function useIncubationTimer(
  onComplete: () => void,
  durationMs = 120000,
  anchorEpoch: number | null = null,
) {
  const [progress, setProgress] = useState(() => {
    if (anchorEpoch == null) return 0;
    const elapsed = Math.max(0, Date.now() - anchorEpoch);
    return Math.min(100, (elapsed / durationMs) * 100);
  });
  const intervalRef = useRef<number | undefined>(undefined);
  const onCompleteRef = useRef(onComplete);
  const completedRef = useRef(false);

  // Keep callback ref up to date without retriggering the timer effect
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    completedRef.current = false;

    if (anchorEpoch == null) {
      setProgress(0);
      return;
    }

    // Compute elapsed at effect-run time (not at render time) so the timer
    // resets correctly even when two different anchors both produce ~0 ms of
    // computed elapsed during the React render phase.
    const elapsedAtStart = Math.max(0, Date.now() - anchorEpoch);
    setProgress(Math.min(100, (elapsedAtStart / durationMs) * 100));

    // If already past full duration, fire immediately
    if (elapsedAtStart >= durationMs) {
      setProgress(100);
      completedRef.current = true;
      onCompleteRef.current();
      return;
    }

    intervalRef.current = setInterval(() => {
      if (completedRef.current) return;

      const elapsed = Math.max(0, Date.now() - anchorEpoch);
      const newProgress = Math.min((elapsed / durationMs) * 100, 100);
      setProgress(newProgress);

      if (newProgress >= 100) {
        completedRef.current = true;
        clearInterval(intervalRef.current);
        onCompleteRef.current();
      }
    }, 500);

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [durationMs, anchorEpoch]);

  return progress;
}
